import React from "react";
import "./AnswerSidebar.css";
import "../../common/MentionText.css";

import { useSelector, useDispatch } from "react-redux";
import {
  selectSidebarAnswer,
  selectUIStates,
  setOnDiscardConfirm,
  setUIStates,
} from "../../../redux/features/appData";
import { formatDate, PlatformSource, getDefaultUserAvatar } from "winslow-core";
import { AnswerSidebarResource } from "./AnswerSidebarResource";
import { handleCopyText } from "../../uiHelpers";
import { EmployeeAvatar } from "../../common/EmployeeAvatar";
import { FeedbackRating } from "../../agent-chat/FeedbackRating";
import { useValidatedImageUrl } from "../../../hooks/useValidatedImageUrl";
import { MentionText } from "../../common/MentionText";
import { EscalationIndicator } from "../../common/EscalationIndicator";

interface AnswerSidebarWidgetProps {
  platformSource: PlatformSource;
}

const AnswerSidebarWidget: React.FC<AnswerSidebarWidgetProps> = ({
  platformSource,
}: AnswerSidebarWidgetProps) => {
  return (
    <div className="widget">
      {platformSource === PlatformSource.SLACK && (
        <>
          <img
            src="/icons/slack.svg"
            alt={`Slack Icon`}
            className="widget-logo"
          />
          <div className="widget-text">
            <div className="widget-info">Asked in</div>
            <div className="widget-name">Slack</div>
          </div>
        </>
      )}

      {platformSource === PlatformSource.OUTLOOK && (
        <>
          <img
            src="/icons/outlook.svg"
            alt={`Outlook Icon`}
            className="widget-logo"
          />
          <div className="widget-text">
            <div className="widget-info">Asked in</div>
            <div className="widget-name">Outlook</div>
          </div>
        </>
      )}

      {platformSource === PlatformSource.GMAIL && (
        <>
          <img
            src="/icons/gmail.svg"
            alt={`Gmail Icon`}
            className="widget-logo"
          />
          <div className="widget-text">
            <div className="widget-info">Asked in</div>
            <div className="widget-name">Gmail</div>
          </div>
        </>
      )}

      {platformSource === PlatformSource.TEAMS && (
        <>
          <img
            src="/icons/teams.png"
            alt={`Teams Icon`}
            className="widget-logo"
          />
          <div className="widget-text">
            <div className="widget-info">Asked in</div>
            <div className="widget-name">Teams</div>
          </div>
        </>
      )}

      {(platformSource === PlatformSource.IOS_MOBILE ||
        platformSource === PlatformSource.ANDROID_MOBILE ||
        platformSource === PlatformSource.WEB) && (
        <>
          <img
            src="/icons/winslow-ai-logo.svg"
            alt={`Web Icon`}
            className="widget-logo"
          />
          <div className="widget-text">
            <div className="widget-info">Asked in</div>
            <div className="widget-name">Winslow</div>
          </div>
        </>
      )}
    </div>
  );
};

export const AnswerSidebar: React.FC = () => {
  const dispatch = useDispatch();
  const uiStates = useSelector(selectUIStates);
  const sidebarAnswer = useSelector(selectSidebarAnswer);

  const fallbackAdminAvatar = sidebarAnswer?.adminName
    ? getDefaultUserAvatar(sidebarAnswer.adminName)
    : "";
  const {
    validatedUrl: validatedAdminAvatar,
    isValidating: isValidatingAdminAvatar,
  } = useValidatedImageUrl(
    sidebarAnswer?.adminAvatar || "",
    fallbackAdminAvatar
  );

  const handleClose = () => {
    dispatch(
      setUIStates({
        ...uiStates,
        answerSidebarOpen: false,
      })
    );
  };

  // console.log("sidebarAnswer", sidebarAnswer);

  return (
    <>
      {uiStates.answerSidebarOpen && sidebarAnswer && (
        <div id="answer-sidebar">
          <div className="answer-sidebar-body">
            <div className="question-info">
              <div className="row">
                <div className="question-author">
                  <EmployeeAvatar
                    email={sidebarAnswer.email}
                    name={sidebarAnswer.name}
                    avatarUrl={sidebarAnswer?.avatarURL}
                  />
                  <div className="author-info">
                    <div className="question-from">Question from:</div>
                    <div className="author-name">
                      {sidebarAnswer?.email !== sidebarAnswer?.name
                        ? sidebarAnswer?.name
                        : ""}{" "}
                      {sidebarAnswer?.email && (
                        <span
                          className="author-email"
                          onClick={() => {
                            handleCopyText(sidebarAnswer.email);
                          }}>
                          {sidebarAnswer.email}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div
                  className="answer-sidebar-close"
                  onClick={() => {
                    if (uiStates.snippetEdited) {
                      dispatch(setOnDiscardConfirm(handleClose));
                      dispatch(
                        setUIStates({
                          ...uiStates,
                          confirmDiscardSnippetOpen: true,
                        })
                      );
                    } else {
                      handleClose();
                    }
                  }}>
                  <img
                    className="close-icon"
                    src="/icons/close-icon.svg"
                    alt="Close"
                  />
                </div>
              </div>
              <div className="question-label-row row">
                <div className="question-label">Question</div>
                {sidebarAnswer.requiredEscalation && (
                  <div className="question-escalation">
                    <EscalationIndicator size={16} />
                  </div>
                )}
                <div className="question-label-date">
                  {sidebarAnswer?.createdAt &&
                    formatDate(new Date(sidebarAnswer?.createdAt))}
                </div>
              </div>
              <div className="question-text">
                <MentionText text={sidebarAnswer?.question || ""} />
              </div>

              <div className="answer-text">
                <MentionText text={sidebarAnswer?.answer || ""} />
              </div>
            </div>

            {sidebarAnswer?.message?.id && (
              <>
                <div className="details-header">Ratings</div>
                <div className="answer-rating">
                  <FeedbackRating
                    messageId={sidebarAnswer.message.id}
                    feedbackRating={sidebarAnswer.message.feedbackRating as any}
                    alwaysVisible={true}
                  />
                </div>
                <div className="divider"></div>
              </>
            )}

            <div className="details-header">Details</div>

            <div className="widgets-container">
              {sidebarAnswer?.adminAvatar && sidebarAnswer?.adminName && (
                <div className="widget">
                  {isValidatingAdminAvatar ? (
                    <div className="widget-avatar avatar-loading">...</div>
                  ) : (
                    <img
                      src={validatedAdminAvatar}
                      alt={`${sidebarAnswer?.adminName}'s Avatar`}
                      className="widget-avatar"
                    />
                  )}

                  <div className="widget-text">
                    <div className="widget-info">Serviced By</div>
                    <div className="widget-name">
                      {sidebarAnswer?.adminName}
                    </div>
                  </div>
                </div>
              )}

              {sidebarAnswer?.platformSource && (
                <>
                  <AnswerSidebarWidget
                    platformSource={sidebarAnswer.platformSource}
                  />
                </>
              )}
            </div>
            <div className="resources-section">
              {sidebarAnswer &&
                sidebarAnswer?.resources &&
                sidebarAnswer.resources.length > 0 && (
                  <div className="details-header">Resources</div>
                )}
              {sidebarAnswer?.resources &&
                sidebarAnswer.resources.map((resource) => (
                  <AnswerSidebarResource
                    key={resource.id}
                    link={resource}
                    collectionId={sidebarAnswer.collectionId}
                  />
                ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
};
